import React, { useEffect, useRef, useState } from "react";
import Player from "@vimeo/player";
import { ExternalLink, Film } from "lucide-react";
import api, { mediaUrl } from "../lib/api";

// Robust video player. First tries the tier-gated direct stream (backend +
// Vimeo API token — plays on any domain). Falls back to the Vimeo embed; if
// the video is domain-blocked, shows a graceful fallback with the lesson link.
export default function VideoPlayer({ embedUrl, sourceUrl, title, itemId, autoPlay = true }) {
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | playing | blocked
  const [streamUrl, setStreamUrl] = useState(undefined); // undefined=checking, null=unavailable

  useEffect(() => {
    let live = true;
    if (!itemId) { setStreamUrl(null); return; }
    api.get(`/videos/${itemId}/stream`)
      .then((r) => { if (live) setStreamUrl(mediaUrl(r.data.url)); })
      .catch(() => { if (live) setStreamUrl(null); });
    return () => { live = false; };
  }, [itemId]);

  useEffect(() => {
    if (streamUrl || streamUrl === undefined && embedUrl?.startsWith("/api/media/")) return;
    if (!embedUrl || embedUrl.startsWith("/api/media/")) { setStatus("blocked"); return; }
    let player;
    let settled = false;
    const el = containerRef.current;
    if (el) el.innerHTML = "";

    const watchdog = setTimeout(() => {
      if (!settled) { settled = true; setStatus("blocked"); }
    }, 6500);

    try {
      const id = (embedUrl.match(/video\/(\d+)/) || [])[1];
      player = new Player(el, {
        id: id ? Number(id) : undefined,
        url: id ? undefined : embedUrl,
        responsive: true,
        dnt: true,
        title: false,
        byline: false,
        portrait: false,
      });
      player.ready().then(() => {
        if (!settled) { settled = true; clearTimeout(watchdog); setStatus("playing"); }
      }).catch(() => {
        if (!settled) { settled = true; clearTimeout(watchdog); setStatus("blocked"); }
      });
      player.on("error", () => {
        if (!settled) { settled = true; clearTimeout(watchdog); setStatus("blocked"); }
      });
    } catch {
      setStatus("blocked");
    }

    return () => { clearTimeout(watchdog); try { player && player.destroy(); } catch { /* noop */ } };
  }, [embedUrl, streamUrl]);

  if (streamUrl) {
    return (
      <div className="relative w-full aspect-video bg-black">
        <video src={streamUrl} controls autoPlay={autoPlay} controlsList="nodownload" disablePictureInPicture
          onContextMenu={(e) => e.preventDefault()} onError={() => setStreamUrl(null)}
          className="w-full h-full" data-testid="video-player-native" />
      </div>
    );
  }

  return (
    <div className="relative w-full aspect-video bg-black">
      <div ref={containerRef} className="w-full h-full [&_iframe]:w-full [&_iframe]:h-full" data-testid="video-player-frame" />

      {status !== "playing" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-brand-ink">
          {status === "loading" ? (
            <>
              <div className="w-10 h-10 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              <p className="text-white/70 text-sm mt-4">Loading “{title}”…</p>
            </>
          ) : (
            <>
              <Film className="text-brand-gold" size={30} />
              <p className="text-white font-medium mt-3">This lesson is protected content</p>
              <p className="text-white/60 text-xs mt-1 max-w-sm">Seymour's private videos stream inside the portal once this domain is authorized in Vimeo. Until then, you can watch the full lesson on isoharts.com.</p>
              {sourceUrl && (
                <a href={sourceUrl} target="_blank" rel="noreferrer" data-testid="video-source-link"
                  className="btn-gold mt-4 !py-2 text-sm">Watch the lesson <ExternalLink size={15} /></a>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
